import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Robot, Power, Warning, Clock, PaperPlaneTilt, ArrowClockwise, CheckCircle, XCircle, PauseCircle } from "@phosphor-icons/react";

const CHANNEL_LABELS = { email: "Email", whatsapp: "WhatsApp", sms: "SMS", in_app: "In-App" };

export default function AdminAiOutreach() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ action_types: [], channels: [] });
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: st }] = await Promise.all([
        api.get("/ai-outreach/settings"), api.get("/ai-outreach/stats"),
      ]);
      setCfg(s.settings); setMeta({ action_types: s.action_types, channels: s.channels }); setStats(st);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try { await api.post("/ai-outreach/settings", patch);
      setCfg((c) => ({ ...c, ...patch })); toast.success("Saved"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai-outreach/run", {});
      toast.success(`Cycle ran · sent ${data.result.sent || 0} · detected ${data.result.detected || 0}`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const followUpAll = async () => {
    if (!window.confirm("Queue a follow-up to every member now? Each still respects consent, quiet hours and daily limits.")) return;
    setBusy(true);
    try { const { data } = await api.post("/ai-outreach/follow-up-all", {});
      toast.success(`Queued follow-ups for ${data.queued} of ${data.members} members`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const [bcSubject, setBcSubject] = useState("");
  const [bcMessage, setBcMessage] = useState("");
  const sendBroadcast = async (test = false) => {
    if (bcSubject.trim().length < 2 || bcMessage.trim().length < 2) return toast.error("Add a subject and a message");
    if (!test && !window.confirm("Send this message to every opted-in member now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai-outreach/broadcast", { subject: bcSubject, message: bcMessage, test });
      toast.success(test ? `Test sent to ${data.sent_to}` : `Broadcast sending to ${data.recipients} opted-in members`);
      if (!test) { setBcSubject(""); setBcMessage(""); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const [waTemplate, setWaTemplate] = useState("");
  const sendWaBlast = async () => {
    if (!waTemplate.trim()) return toast.error("Enter an approved WhatsApp template name");
    if (!window.confirm("Send this WhatsApp template to all opted-in members with WhatsApp consent?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai-sales/wa-blast", { template_name: waTemplate });
      toast.success(`WhatsApp campaign sending to ${data.audience} members`);
      setWaTemplate("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="py-16 text-center text-muted-foreground" data-testid="outreach-loading">Loading…</div>;

  const channels = cfg.ai_outreach_channels || {};
  const actions = cfg.ai_outreach_actions || {};
  const setChannel = (k, v) => save({ ai_outreach_channels: { ...channels, [k]: v } });
  const setAction = (k, v) => save({ ai_outreach_actions: { ...actions, [k]: v } });

  return (
    <div className="space-y-6" data-testid="admin-ai-outreach">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><Robot size={28} weight="fill" /> SI Sales — Automation</h1>
          <p className="mt-1 text-sm text-muted-foreground">Proactive follow-ups on autopilot. Everything here is enforced on the server — consent, quiet hours and limits always apply.</p>
        </div>
        <button data-testid="outreach-run-now" onClick={runNow} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold hover:border-primary disabled:opacity-60">
          <ArrowClockwise size={16} weight="bold" /> Run cycle now
        </button>
        <button data-testid="outreach-followup-all" onClick={followUpAll} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
          <PaperPlaneTilt size={16} weight="fill" /> Follow up with all members
        </button>
      </div>

      {/* one-off broadcast blast to all opted-in members */}
      <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="outreach-broadcast">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><PaperPlaneTilt size={18} weight="fill" /> Broadcast to all opted-in members</h2>
        <p className="mt-1 text-xs text-muted-foreground">Write one message and email every member who has opted in to iYeneh marketing. Opted-out members are skipped automatically.</p>
        <input data-testid="broadcast-subject" value={bcSubject} onChange={(e) => setBcSubject(e.target.value)}
          placeholder="Subject line" maxLength={140}
          className="mt-3 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <textarea data-testid="broadcast-message" value={bcMessage} onChange={(e) => setBcMessage(e.target.value)}
          rows={4} placeholder="Your message to members…" maxLength={5000}
          className="mt-2 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        {(bcSubject || bcMessage) && (
          <div data-testid="broadcast-preview" className="mt-3 rounded-xl border border-white/10 bg-background/60 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Live preview</p>
            <p className="mt-1 text-sm font-semibold">{bcSubject || "(subject)"}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{bcMessage || "(your message)"}</p>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="broadcast-test" onClick={() => sendBroadcast(true)} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold hover:border-primary disabled:opacity-60">
            Send test to me
          </button>
          <button data-testid="broadcast-send" onClick={() => sendBroadcast(false)} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
            <PaperPlaneTilt size={16} weight="fill" /> Send broadcast
          </button>
        </div>
      </div>

      {/* WhatsApp template campaign (paid add-on) */}
      <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="outreach-wa-blast">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><PaperPlaneTilt size={18} weight="fill" /> WhatsApp template campaign</h2>
        <p className="mt-1 text-xs text-muted-foreground">Bulk-send a pre-approved WhatsApp template to opted-in members with WhatsApp consent. Requires the SMS &amp; WhatsApp Marketing add-on.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="wa-template-name" value={waTemplate} onChange={(e) => setWaTemplate(e.target.value)}
            placeholder="approved_template_name" className="flex-1 min-w-[200px] rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <button data-testid="wa-blast-send" onClick={sendWaBlast} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">Send WhatsApp campaign</button>
        </div>
      </div>

      {/* master + kill switch */}
      <div className="grid gap-4 md:grid-cols-2">
        <ToggleCard testid="outreach-enabled" icon={Power} title="Proactive SI Sales"
          desc="Master switch for all automated outreach." on={!!cfg.ai_outreach_enabled}
          onChange={(v) => save({ ai_outreach_enabled: v })} />
        <ToggleCard testid="outreach-kill" icon={Warning} danger title="Emergency kill switch"
          desc="Instantly blocks every automated send when ON." on={!!cfg.ai_outreach_kill_switch}
          onChange={(v) => save({ ai_outreach_kill_switch: v })} />
      </div>

      {/* scheduler status — proves the autonomous cycle is running in production */}
      {stats?.scheduler && (
        <div className={`rounded-2xl border p-5 ${stats.scheduler.cron_secret_configured ? "border-white/10 bg-card" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="outreach-scheduler">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock size={20} weight="fill" className={stats.scheduler.cron_secret_configured ? "text-primary" : "text-amber-500"} />
              <div>
                <h3 className="font-semibold">Autonomous scheduler {stats.scheduler.enabled ? "active" : "paused"}</h3>
                <p className="text-sm text-muted-foreground">
                  {stats.scheduler.last_run
                    ? `Last cycle ${(stats.scheduler.last_run.at || "").slice(0, 16).replace("T", " ")} · sent ${stats.scheduler.last_run.counts?.sent || 0} · detected ${stats.scheduler.last_run.counts?.detected || 0}`
                    : "No autonomous cycle has run yet."}
                </p>
              </div>
            </div>
            {!stats.scheduler.cron_secret_configured && (
              <span data-testid="outreach-cron-warning" className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500">
                Set WEBHOOK_CRON_SECRET in production to run automatically
              </span>
            )}
          </div>
        </div>
      )}

      {/* stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="outreach-stats">
          <Stat label="Scheduled" v={stats.stats.scheduled} icon={Clock} />
          <Stat label="Due now" v={stats.stats.due_now} icon={Clock} />
          <Stat label="Sent" v={stats.stats.sent} icon={PaperPlaneTilt} good />
          <Stat label="Converted" v={stats.stats.converted} icon={CheckCircle} good />
          <Stat label="Suppressed" v={stats.stats.suppressed} icon={PauseCircle} />
          <Stat label="Failed" v={stats.stats.failed} icon={XCircle} bad />
          <Stat label="Skipped" v={stats.stats.skipped} icon={PauseCircle} />
        </div>
      )}

      {/* channels */}
      <Section title="Channels" desc="Which channels the engine may use. Email is live; enable others once their sender is verified.">
        <div className="flex flex-wrap gap-2">
          {meta.channels.map((c) => (
            <Pill key={c} testid={`outreach-channel-${c}`} active={!!channels[c]} onClick={() => setChannel(c, !channels[c])} disabled={busy}>
              {CHANNEL_LABELS[c] || c}
            </Pill>
          ))}
        </div>
      </Section>

      {/* action types */}
      <Section title="Action types" desc="Turn individual Next-Best-Action journeys on or off.">
        <div className="flex flex-wrap gap-2">
          {meta.action_types.filter((a) => a !== "NO_ACTION").map((a) => (
            <Pill key={a} testid={`outreach-action-${a}`} active={!!actions[a]} onClick={() => setAction(a, !actions[a])} disabled={busy}>
              {a.replace(/_/g, " ")}
            </Pill>
          ))}
        </div>
      </Section>

      {/* limits & quiet hours */}
      <Section title="Quiet hours & limits" desc="Member-local quiet hours and frequency caps. Applied server-side to every send.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <NumField testid="outreach-quiet-start" label="Quiet start (hour, 0–23)" value={cfg.ai_outreach_quiet_start} onSave={(v) => save({ ai_outreach_quiet_start: v })} />
          <NumField testid="outreach-quiet-end" label="Quiet end (hour, 0–23)" value={cfg.ai_outreach_quiet_end} onSave={(v) => save({ ai_outreach_quiet_end: v })} />
          <TextField testid="outreach-tz" label="Fallback timezone" value={cfg.ai_outreach_default_tz} onSave={(v) => save({ ai_outreach_default_tz: v })} />
          <NumField testid="outreach-max-day" label="Max per member / day" value={cfg.ai_outreach_max_per_day} onSave={(v) => save({ ai_outreach_max_per_day: v })} />
          <NumField testid="outreach-max-week" label="Max per member / week" value={cfg.ai_outreach_max_per_week} onSave={(v) => save({ ai_outreach_max_per_week: v })} />
        </div>
      </Section>

      {/* journey timing */}
      <Section title="Journey timing" desc="When the engine schedules each proactive journey. Enable a journey above under Action types.">
        <div className="grid gap-4 sm:grid-cols-3">
          <NumField testid="outreach-onboard-delay" label="Onboarding delay (minutes after signup)" value={cfg.ai_outreach_onboarding_delay_minutes} onSave={(v) => save({ ai_outreach_onboarding_delay_minutes: v })} />
          <NumField testid="outreach-crosssell-delay" label="Cross-sell delay (days after purchase)" value={cfg.ai_outreach_cross_sell_delay_days} onSave={(v) => save({ ai_outreach_cross_sell_delay_days: v })} />
          <NumField testid="outreach-winback-days" label="Win-back after inactivity (days)" value={cfg.ai_outreach_winback_days} onSave={(v) => save({ ai_outreach_winback_days: v })} />
        </div>
        <a href="/command/ai-sales/journeys" data-testid="outreach-goto-journeys" className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">Open Member Journeys →</a>
      </Section>

      {/* abandoned checkout */}
      <Section title="Abandoned-checkout rescue" desc="Follow up on SI-sales checkouts that were started but not paid.">
        <div className="mb-4"><ToggleInline testid="outreach-rescue-enabled" label="Rescue enabled" on={!!cfg.ai_checkout_rescue_enabled} onChange={(v) => save({ ai_checkout_rescue_enabled: v })} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumField testid="outreach-abandon-min" label="Wait before rescue (minutes)" value={cfg.ai_checkout_abandon_min_minutes} onSave={(v) => save({ ai_checkout_abandon_min_minutes: v })} />
          <NumField testid="outreach-abandon-max" label="Stop rescuing after (hours)" value={cfg.ai_checkout_abandon_max_hours} onSave={(v) => save({ ai_checkout_abandon_max_hours: v })} />
        </div>
      </Section>

      {/* recent activity */}
      {stats?.recent?.length > 0 && (
        <Section title="Recent outreach" desc="Latest automated actions across all members.">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="outreach-recent">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2 pr-3">Action</th><th className="pr-3">Product</th><th className="pr-3">Channel</th><th className="pr-3">Status</th><th className="pr-3">Reason</th><th className="pr-3">When</th>
              </tr></thead>
              <tbody>
                {stats.recent.slice(0, 25).map((r) => (
                  <tr key={r._id} className="border-t border-white/5">
                    <td className="py-2 pr-3">{(r.action_type || "").replace(/_/g, " ")}</td>
                    <td className="pr-3">{r.product}</td>
                    <td className="pr-3">{r.channel || "—"}</td>
                    <td className="pr-3"><StatusBadge s={r.status} converted={r.converted} /></td>
                    <td className="pr-3 text-muted-foreground">{r.suppression_reason || r.failure_reason || "—"}</td>
                    <td className="pr-3 text-muted-foreground">{(r.sent_at || r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function ToggleCard({ icon: Icon, title, desc, on, onChange, danger, testid }) {
  return (
    <div className={`flex items-start justify-between gap-4 rounded-2xl border p-5 ${danger && on ? "border-destructive/50 bg-destructive/5" : "border-white/10 bg-card"}`}>
      <div className="flex gap-3">
        <Icon size={22} weight="fill" className={danger ? "text-destructive" : "text-primary"} />
        <div><h3 className="font-semibold">{title}</h3><p className="text-sm text-muted-foreground">{desc}</p></div>
      </div>
      <input data-testid={testid} type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-5 w-9 accent-primary" />
    </div>
  );
}
function ToggleInline({ label, on, onChange, testid }) {
  return <label className="flex w-max items-center gap-2 text-sm font-medium"><input data-testid={testid} type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="h-5 w-9 accent-primary" /> {label}</label>;
}
function Section({ title, desc, children }) {
  return <div className="rounded-2xl border border-white/10 bg-card p-5"><h2 className="font-display text-lg font-semibold">{title}</h2>{desc && <p className="mb-4 mt-0.5 text-sm text-muted-foreground">{desc}</p>}{children}</div>;
}
function Pill({ children, active, onClick, disabled, testid }) {
  return <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium capitalize transition-colors disabled:opacity-50 ${active ? "border-primary bg-primary text-primary-foreground" : "border-white/10 text-muted-foreground hover:border-primary"}`}>{children}</button>;
}
function Stat({ label, v, icon: Icon, good, bad }) {
  return <div className="rounded-2xl border border-white/10 bg-card p-4"><Icon size={18} className={good ? "text-emerald-500" : bad ? "text-destructive" : "text-muted-foreground"} /><p className="mt-2 font-display text-2xl font-semibold">{v ?? 0}</p><p className="text-xs text-muted-foreground">{label}</p></div>;
}
function NumField({ label, value, onSave, testid }) {
  return (
    <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <input data-testid={testid} type="number" defaultValue={value} key={`${testid}-${value}`}
        onBlur={(e) => { const v = parseInt(e.target.value || 0, 10); if (v !== value) onSave(v); }}
        className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
    </label>
  );
}
function TextField({ label, value, onSave, testid }) {
  return (
    <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <input data-testid={testid} defaultValue={value} key={`${testid}-${value}`}
        onBlur={(e) => { if (e.target.value !== value) onSave(e.target.value); }}
        className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
    </label>
  );
}
function StatusBadge({ s, converted }) {
  const map = { sent: "text-emerald-500", scheduled: "text-sky-500", deferred: "text-amber-500", suppressed: "text-muted-foreground", failed: "text-destructive", skipped: "text-muted-foreground", sending: "text-sky-500" };
  return <span className={`font-medium ${map[s] || ""}`}>{converted ? "converted" : s}</span>;
}
